import React, { ReactElement, useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import ChampFormulaire from '../../Common/ChampFormulaire';
import CheckboxFormulaire from '../../Common/CheckboxFormulaire';
import DropdownEntiteFormulaire from '../../Common/DropdownEntiteFormulaire';
import ChampCascade, { FilterInfo, InjectionReference } from '../../Common/ChampCascade';
import FormulaireModificationEntite from '../Utils/FormulaireModificationEntite';
import {
    Activite,
    Fichier,
    Fournisseur,
    TypeActivite,
    MethodePaiement,
    BaseEntite,
    Contact,
    HistoriqueLogs,
} from '../../../../../common/src/entities';
import { DateTime } from 'luxon';
import { Badge, Col, Dropdown, Row } from 'react-bootstrap';
import { schemaActivite } from '../../../../../common/src/schemas';
import api, { useEntites, useGetQuery } from '../../../Utils/api';
import { FormikHelpers, useFormikContext } from 'formik';
import ChampMultipleContact from '../Contacts/ChampMultipleContact';
import ChampMultipleThematique from '../Thematiques/ChampMultipleThematique';
import { toISODate, compareDate } from '../../../Utils/dateUtils';
import ChampMultipleImage from '../Images/ChampMultipleImage';
import { AxiosResponse } from 'axios';
import ChampFichier from '../Images/ChampFichier';
import { useLoadPdf } from '../../Vitrine/Activites/PDF/LoadPDF';
import ChampMultipleFournisseur from '../Fournisseurs/ChampMultipleFournisseur';
import ChampMultipleContactActivite from '../Contacts/ChampMultipleContactActivite';
import ChampMultipleLieuActivite from '../Lieux/ChampMultipleActiviteLieu';
import { ModalDetails } from '../../Common/ModalDetails';
import ChampsMultipleLangue from '../Langues/ChampMultipleLangue';
import ChampMultipleLieu from '../Lieux/ChampMultipleLieu';
import Images from '../../../../../common/src/entities/image.entity';
import { AiFillLock } from 'react-icons/all';
import PopoverSimple from '../../Common/PopoverSimple';
import { OptionsCovid } from '../Options/Options';
import { useRecoilState } from 'recoil';
import { etatMasquerCovid19 } from '../../../State';
import ChampMultipleEtiquette from '../Etiquettes/ChampsMultipleEtiquette';
import ChampMultipleActiviteProduit from '../Produits/ChampsMultipleActiviteProduit';
import DynamicTableResumeProduits from '../Produits/DynamicTableResumeProduits';
import LogsLoader, { dateFormatter } from '../Utils/Log';
import { Tooltip } from '../../Common/Tooltip';
import { EtatEntite } from '../../Common/EtatEntite';
import CheckboxFormulaireEtatEntite from '../../Common/CheckboxFormulaireEtatEntite';
import { TitreCarte } from '../../Common/TitreCarte';
import { GROUPES_AGES, LABEL_COL, SUGGESTIONS_LIEU } from '../constantes';
import { MyPaper } from '../Utils/MyPaper';
import ChampMultipleModelesHoraireGlissable from '../ModelesHoraire/ChampMultipleModelesHoraireGlissable';

interface IChampsState<T> {
    value: T;
}

interface IChampsActivitesState {
    public: IChampsState<boolean>;
    public_date_echeance: IChampsState<string | undefined>;
    fournisseur: IChampsState<Fournisseur | undefined>;
}

const SUGGESTIONS_CONTACT = [
    'Coordonnateur des loisir',
    'Directeur',
    'Directeur adjoint',
    'Directrice',
    'Directrice adjointe',
    'Directrice des loisirs',
    'Enseignant',
    "Enseignant d'éducation physique",
    'Enseignante',
    "Enseignante d'éducation physique",
    'Éducateur',
    'Éducatrice',
    'Éducatrice classe principale',
    'Responsable SDG',
    'Secrétaire',
    'Service de garde',
    'TES',
    'Technicien en loisirs',
    'Technicienne en Loisirs',
];

export function ChampsActivites(): ReactElement {
    const { data } = useEntites(Fournisseur);
    const { id } = useParams() as { id: string };
    const { values, setFieldValue } = useFormikContext<Activite>();

    const [state, setState] = useState<IChampsActivitesState>({
        public: {
            value: values.public,
        } as IChampsState<boolean>,
        public_date_echeance: {
            value: values.public_date_echeance,
        } as IChampsState<string | undefined>,
        fournisseur: {
            value: values.fournisseur as Fournisseur,
        } as IChampsState<Fournisseur | undefined>,
    } as IChampsActivitesState);

    const [afficherModalDetailsLieu, setAfficherModalDetailsLieu] = useState<boolean>(false);
    const [indexModalDetailsLieu, setIndexModalDetailsLieu] = useState<number>(0);

    useEffect(() => {
        setState({
            public: {
                value: values.public,
            } as IChampsState<boolean>,
            public_date_echeance: {
                value: values.public_date_echeance,
            } as IChampsState<string | undefined>,
            fournisseur: {
                value: values.fournisseur as Fournisseur,
            } as IChampsState<Fournisseur | undefined>,
        } as IChampsActivitesState);
    }, [values]);

    const methodePaiementReferenceInjection: InjectionReference<Fournisseur, BaseEntite> = {
        ref: state.fournisseur.value as Fournisseur,
        filterInfo: {
            key: 'fk_methode_paiement' as keyof Fournisseur,
            notes: 'Méthode de paiement',
        } as FilterInfo<Fournisseur>,
        modelSommaire: MethodePaiement,
        notes: '@Fournisseur',
    } as InjectionReference<Fournisseur, BaseEntite>;

    const nomAccesDReferenceInjection: InjectionReference<Fournisseur> = {
        ref: state.fournisseur.value as Fournisseur,
        filterInfo: {
            key: 'nom_acces_d' as keyof Fournisseur,
            notes: 'Nom dans AccesD',
        } as FilterInfo<Fournisseur>,
        notes: '@Fournisseur',
    } as InjectionReference<Fournisseur>;

    const contactParDefautReferenceInjection: InjectionReference<Contact, Contact, Contact> = {
        entite: Contact,
        ref: {
            filterInfo: {
                key: 'id' as keyof Contact,
                value: (state.fournisseur.value as Fournisseur)?.fk_id_contact_par_defaut,
                notes: 'Contact par défaut',
            } as FilterInfo<Contact>,
            modelSommaire: Contact,
            notes: '[@Fournisseur]',
        } as InjectionReference<Contact, BaseEntite, Contact>,
        notes: 'Contacts',
    } as InjectionReference<Contact, Contact, Contact>;

    const dateSuiviReferenceInjection: InjectionReference<Fournisseur> = {
        ref: state.fournisseur.value as Fournisseur,
        filterInfo: {
            key: 'trigger_suivi' as keyof Fournisseur,
            notes: 'Date de suivi',
        } as FilterInfo<Fournisseur>,
        notes: '@Fournisseur',
    } as InjectionReference<Fournisseur>;

    const notesReferenceInjection: InjectionReference<Fournisseur> = {
        ref: state.fournisseur.value as Fournisseur,
        filterInfo: {
            key: 'notes' as keyof Fournisseur,
            notes: 'Notes',
        } as FilterInfo<Fournisseur>,
        notes: '@Fournisseur',
    } as InjectionReference<Fournisseur>;

    const pctCommissionDefautReferenceInjection: InjectionReference<Fournisseur> = {
        ref: state.fournisseur.value as Fournisseur,
        filterInfo: {
            key: 'pct_commission_defaut' as keyof Fournisseur,
            notes: 'Commission par défaut (%)',
        } as FilterInfo<Fournisseur>,
        notes: '@Fournisseur',
    } as InjectionReference<Fournisseur>;

    const publicFormulaireNotifyChangeHandler = (isChecked: boolean) => {
        let publicDateEcheance: string | undefined | null = state.public_date_echeance.value;
        let newState: IChampsActivitesState = { ...state } as IChampsActivitesState;

        if (!isChecked && publicDateEcheance) {
            const currentDate: string = toISODate(new Date().toISOString());
            if (compareDate(publicDateEcheance as string, currentDate) < 1) {
                publicDateEcheance = ''; // must be empty string if want UI to clear field. Null would reset the value, but not the UI until refresh
                setFieldValue('public_date_echeance', publicDateEcheance);
            }
            newState = {
                ...state,
                public: {
                    ...state.public,
                    value: isChecked,
                },
                public_date_echeance: {
                    ...state.public_date_echeance,
                    value: publicDateEcheance,
                },
            } as IChampsActivitesState;
        }
        setState(newState);
    };

    const publicDateEcheanceFormulaireNotifyChangeHandler = (newDate: any) => {
        setState({
            ...state,
            public_date_echeance: {
                ...state.public_date_echeance,
                value: newDate ? toISODate(newDate as string) : undefined,
            },
        } as IChampsActivitesState);
    };

    const publicDateEcheanceFormulaireValidateOnChangeHandler = (newDate?: string) => {
        const currentDate: string = toISODate(new Date().toISOString());
        const selectedDate: string | null = newDate ? toISODate(newDate) : null;
        let isFieldValid = true;
        if (state.public.value && selectedDate && compareDate(selectedDate, currentDate) < 1) {
            isFieldValid = false;
        }
        return isFieldValid;
    };

    const [afficherModalContactActivite, setAfficherModalContactActivite] = useState<boolean>(false);
    const [indexModalContactActivite, setIndexModalContactActivite] = useState<number>(0);

    const queryCovid = useGetQuery<OptionsCovid>(['options/covid']);
    const [estMasquer, setEstMasquer] = useState(queryCovid.data?.texte === '1');

    useEffect(() => {
        if (queryCovid.data) {
            setEstMasquer(queryCovid.data?.texte === '1');
        }
    }, [queryCovid.data]);

    function ouvrirModalContactActivite(index: number): void {
        setAfficherModalContactActivite(true);
        setIndexModalContactActivite(index);
    }

    function ouvrirModalDetailsLieu(index: number): void {
        setAfficherModalDetailsLieu(true);
        setIndexModalDetailsLieu(index);
    }

    return (
        <>
            <div className="pb-5">
                <EtatEntite>
                    <CheckboxFormulaireEtatEntite
                        id="actif"
                        titre={
                            <div className="d-flex align-items-center">
                                Actif&nbsp;
                                {data?.find((f) => f.id == values.fk_id_fournisseur)?.actif ?? true ? null : (
                                    <Badge variant="warning">Fournisseur inactif</Badge>
                                )}
                            </div>
                        }
                    />
                    <CheckboxFormulaireEtatEntite id="taxable" titre="Taxable" />
                    <CheckboxFormulaireEtatEntite id="ficav" titre="FICAV" />
                    <CheckboxFormulaireEtatEntite id="signer_contrat" titre="Contrat requis" />
                    <CheckboxFormulaireEtatEntite
                        id="public"
                        titre="Publique jusqu'au"
                        notifyChange={publicFormulaireNotifyChangeHandler}
                    />
                    <ChampFormulaire
                        type="date"
                        id="public_date_echeance"
                        parser={(date) => toISODate(date as string)}
                        disabled={false}
                        notifyChange={publicDateEcheanceFormulaireNotifyChangeHandler}
                        validateOnChangeCallback={publicDateEcheanceFormulaireValidateOnChangeHandler}
                        className="d-flex flex-column mx-0 my-1"
                        cacherTitre
                        estPetit
                    />
                </EtatEntite>
                <MyPaper>
                    <ChampFormulaire labelCol={LABEL_COL} id="name" titre="Nom" estPetit />
                    <ChampMultipleFournisseur
                        id="fournisseur"
                        maxSize={1}
                        title="Fournisseur"
                        labelCol={LABEL_COL}
                        avecUnBouton
                    />
                    <ChampCascade
                        injectionRef={contactParDefautReferenceInjection}
                        injectionTitle="Contact par défaut du fournisseur"
                    >
                        <ChampMultipleContact
                            labelCol={LABEL_COL}
                            id="contact_par_defaut"
                            maxSize={1}
                            title="Contact par défaut"
                            avecUnBouton
                        />
                    </ChampCascade>
                    <ChampMultipleContactActivite
                        id="contacts"
                        labelCol={LABEL_COL}
                        avecUnBouton
                        details={formikAvoirDetailContact}
                        suggestions={SUGGESTIONS_CONTACT}
                        placeholder="Detail"
                        clickCrayon={ouvrirModalContactActivite}
                    />
                    <ChampFormulaire labelCol={LABEL_COL} id="priorite" titre="Priorité" type="number" estPetit />
                    <ChampCascade
                        injectionRef={dateSuiviReferenceInjection}
                        injectionTitle="Date de suivi du fournisseur"
                    >
                        <ChampFormulaire
                            type="date"
                            id="trigger_suivi"
                            titre="Date de suivi"
                            labelCol={LABEL_COL}
                            trimDateText
                            parser={(date) => toISODate(date as string)}
                            estPetit
                        />
                    </ChampCascade>
                    <ChampCascade injectionRef={notesReferenceInjection} injectionTitle="Notes du fournisseur">
                        <ChampFormulaire id="notes" titre="Notes internes" textarea labelCol={LABEL_COL} estPetit />
                    </ChampCascade>
                    <ChampFormulaire
                        id="notes_fournisseur"
                        titre={
                            <>
                                Notes pour le fournisseur{' '}
                                <Tooltip tooltip="Visible pour le fournisseur dans les documents." id="tooltip-1" />
                            </>
                        }
                        textarea
                        labelCol={LABEL_COL}
                        estPetit
                    />
                    <ChampFormulaire
                        id="notes_client"
                        titre={
                            <>
                                Notes pour le client{' '}
                                <Tooltip tooltip="Visible pour le client dans les documents." id="tooltip-2" />
                            </>
                        }
                        textarea
                        labelCol={LABEL_COL}
                        estPetit
                    />
                    <ChampCascade
                        injectionRef={pctCommissionDefautReferenceInjection}
                        injectionTitle="Commission par défaut (%) du fournisseur"
                    >
                        <ChampFormulaire
                            id="pct_commission_defaut"
                            titre="Commission par défaut (%)"
                            type="number"
                            labelCol={LABEL_COL}
                            estPetit
                        />
                    </ChampCascade>
                    <ChampMultipleLieu
                        id="adresse_calcul_km"
                        maxSize={1}
                        title="Adresse pour calcul de kilométrage"
                        avecUnBouton
                        labelCol={LABEL_COL}
                    />
                    <ChampFormulaire
                        id="avis_nb_participants"
                        titre="Avis à inclure au moment de la confirmation du nb. de part."
                        labelCol={LABEL_COL}
                        estPetit
                    />
                </MyPaper>
                <TitreCarte>Coordonnées bancaires et information de paiement</TitreCarte>
                <MyPaper>
                    <ChampCascade
                        injectionRef={methodePaiementReferenceInjection}
                        injectionTitle="Méthode de paiement du fournisseur"
                    >
                        <DropdownEntiteFormulaire
                            labelCol={LABEL_COL}
                            id="fk_methode_paiement"
                            titre="Méthode de paiement"
                            entite={MethodePaiement}
                            champs="methode_paiement"
                            estPetit
                            avecUnBouton
                        />
                    </ChampCascade>
                    <ChampCascade
                        injectionRef={nomAccesDReferenceInjection}
                        injectionTitle="Nom dans AccesD du fournisseur"
                    >
                        <ChampFormulaire labelCol={LABEL_COL} id="nom_acces_d" titre="Nom dans AccesD" estPetit />
                    </ChampCascade>
                </MyPaper>
                <TitreCarte>Produit associés à cette activité</TitreCarte>
                <MyPaper>
                    <ChampMultipleActiviteProduit
                        id="activiteProduits"
                        labelCol={LABEL_COL}
                        avecUnBouton
                        details={formikAvoirDetailProduit}
                        placeholder="Detail"
                    />
                    {id ? (
                        <DynamicTableResumeProduits data={values.activiteProduits} />
                    ) : (
                        <p>Recharger la page pour voir le tableau de produit</p>
                    )}
                </MyPaper>
                <TitreCarte>Informations importantes</TitreCarte>
                <MyPaper>
                    <DropdownEntiteFormulaire
                        labelCol={LABEL_COL}
                        id="fk_id_type_activite"
                        titre="Type"
                        champs={'typeActivite'}
                        entite={TypeActivite}
                        estPetit
                    />

                    <ChampFormulaire id="periode_ouverture" titre="Période d'ouverture" labelCol={LABEL_COL} estPetit />
                    <Row>
                        <Col xs={LABEL_COL}>Minimum facturable pour réserver</Col>
                        <Col xs={2}>
                            <ChampFormulaire
                                id="minimum_facturable_nombre"
                                type="number"
                                cacherTitre
                                placeholder="Nombre"
                                estPetit
                            />
                        </Col>
                        <Col xs={3}>
                            <ChampFormulaire
                                id="minimum_facturable_type"
                                suggestions={['participants', '$']}
                                isCreatable
                                cacherTitre
                                placeholder="Unité"
                                labelCol={LABEL_COL}
                                estPetit
                            />
                        </Col>
                        <Col>
                            <ChampFormulaire id="minimum_facturable_texte" cacherTitre placeholder="Avis" estPetit />
                        </Col>
                    </Row>
                    <Row className="pt-3">
                        <Col xs={LABEL_COL}>Maximum de participant</Col>
                        <Col xs={2}>
                            <ChampFormulaire
                                id="max_participants"
                                titre="Nombre"
                                type="number"
                                cacherTitre
                                placeholder="Nombre"
                                estPetit
                            />
                        </Col>
                        <Col xs={3}>
                            <ChampFormulaire
                                id="max_participants_type"
                                suggestions={['participants', '$']}
                                isCreatable
                                cacherTitre
                                placeholder="Unité"
                                labelCol={LABEL_COL}
                                estPetit
                            />
                        </Col>
                        <Col>
                            <ChampFormulaire
                                id="max_participants_texte"
                                titre="Avis concernant le maximum de participants"
                                cacherTitre
                                placeholder="Avis"
                                estPetit
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col xs={LABEL_COL}></Col>
                        <Col>
                            <CheckboxFormulaire id="max_participants_visible" titre="Afficher au public" />
                        </Col>
                    </Row>
                    <ChampFormulaire id="tarifs_estimes" titre="Tarifs estimés" textarea labelCol={LABEL_COL} />
                </MyPaper>
                <TitreCarte>{"Guide d'activité"}</TitreCarte>
                <MyPaper>
                    <ChampFormulaire id="alerte" titre="Avis public" labelCol={LABEL_COL} estPetit />

                    <ChampMultipleEtiquette
                        id="etiquettes"
                        labelCol={LABEL_COL}
                        avecUnBouton={true}
                        formatOptionLabel={(option) => {
                            if (
                                option.etiquette_categorie &&
                                option.etiquette_categorie[0] &&
                                option.etiquette_categorie[0].categorie &&
                                option.etiquette_categorie[0].categorie.name &&
                                option.name
                            ) {
                                return `${option.etiquette_categorie[0].categorie.name} - ${option.name}`;
                            }
                            return 'Introuvable';
                        }}
                    />
                    <ChampMultipleImage
                        labelCol={LABEL_COL}
                        id="fichiers"
                        onRemove={(index) => {
                            if (values.fk_id_image_principale == values.fichiers[index].fk_id_fichier)
                                setFieldValue('fk_id_image_principale', null);
                        }}
                    />
                    <ChampFormulaire
                        titre="Image principale"
                        id="fk_id_image_principale"
                        suggestions={values.fichiers
                            ?.filter((image) => !image.fichiers.name.includes('embed'))
                            .map((image) => ({
                                value: image.fichiers.id,
                                label: image.fichiers.name,
                            }))}
                        notifyChange={(newValue) => {
                            if (values.fichiers) {
                                const selectedFile = values.fichiers.find((file) => file.fk_id_fichier === newValue);
                                if (selectedFile) {
                                    setFieldValue('image_principale', selectedFile.fichiers.name);
                                } else {
                                    setFieldValue('image_principale', null);
                                }
                            }
                        }}
                        labelCol={LABEL_COL}
                        estPetit
                    />
                    <ChampFormulaire type="hidden" id="image_principale" titre="Test" textarea labelCol={LABEL_COL} />

                    <ChampFormulaire id="description" titre="Description" textarea labelCol={LABEL_COL} />
                </MyPaper>
                <MyPaper>
                    <ChampFormulaire
                        id="covid"
                        titre={
                            <>
                                {' '}
                                Adapté covid{' '}
                                {estMasquer && (
                                    <PopoverSimple
                                        titre={'Covid-19 Masquer'}
                                        texte={
                                            <div>
                                                <p>
                                                    Pour gérer le masquage de la section COVID, veuillez cliquer sur le
                                                    cadenas.
                                                </p>
                                            </div>
                                        }
                                    >
                                        <a href="http://localhost:3000/dashboard/options">
                                            <AiFillLock></AiFillLock>{' '}
                                        </a>
                                    </PopoverSimple>
                                )}
                            </>
                        }
                        isCreatable
                        suggestions={
                            estMasquer
                                ? [
                                      "Aucun choix, car la section Covid-19 est masquée, survoler le cadenas bleu pour plus d'information",
                                  ]
                                : ['N/A', 'Oui', 'Non']
                        }
                        labelCol={LABEL_COL}
                        estPetit
                        disabled={estMasquer}
                    />

                    <ChampFormulaire
                        id="covid_description"
                        titre={
                            <>
                                {' '}
                                Description covid{' '}
                                {estMasquer && (
                                    <PopoverSimple
                                        titre={'Covid-19 Masquer'}
                                        texte={
                                            <div>
                                                <p>
                                                    Pour gérer le masquage de la section COVID, veuillez cliquer sur le
                                                    cadenas.
                                                </p>
                                            </div>
                                        }
                                    >
                                        <a href="http://localhost:3000/dashboard/options">
                                            <AiFillLock></AiFillLock>{' '}
                                        </a>
                                    </PopoverSimple>
                                )}
                            </>
                        }
                        textarea
                        labelCol={LABEL_COL}
                        disabled={estMasquer}
                    />
                </MyPaper>
                <MyPaper>
                    <ChampMultipleThematique labelCol={LABEL_COL} id="thematiques" />
                    <ChampMultipleLieuActivite
                        id="lieux"
                        labelCol={LABEL_COL}
                        avecUnBouton
                        details={formikAvoirDetailLieu}
                        suggestions={SUGGESTIONS_LIEU}
                        placeholder="Detail"
                        clickCrayon={ouvrirModalDetailsLieu}
                    />

                    <ChampFormulaire
                        id="guide_type_accueil"
                        titre="Type d'accueil"
                        isCreatable
                        suggestions={[
                            "Arrivés sur place, vous serez accueillis dans l'autobus.",
                            "Arrivés sur place, rendez-vous à l'accueil.",
                        ]}
                        labelCol={LABEL_COL}
                        estPetit
                    />
                    <ChampFormulaire
                        id="guide_creation_horaire"
                        titre="Confirmation de l'horaire"
                        isCreatable
                        suggestions={[
                            "Solution Animation finalisera l'horaire après avoir reçu la confirmation du nombre de participants.",
                            "Le fournisseur de l'activité créera l'horaire détaillé après avoir reçu la confirmation du nombre de participants.",
                            "Le nombre de participants confirmés ne devrait pas affecter l'horaire prévu.",
                            "Le nombre de participants confirmés ne devrait pas affecter l'horaire prévu. L'horaire détaillé de la journée peut vous être remis sur place. ",
                        ]}
                        labelCol={LABEL_COL}
                        estPetit
                    />
                    <ChampFormulaire
                        id="public_cible"
                        titre="Public cible"
                        isCreatable
                        suggestions={GROUPES_AGES}
                        labelCol={LABEL_COL}
                        estPetit
                    />
                    <ChampFormulaire
                        id="guide_minimum_facturable_jour_anim_client"
                        titre="Min. fact. jour de l'animation (pour client)"
                        isCreatable
                        suggestions={[
                            "Le dernier nombre de participants confirmé sauf si plus élevé lors de l'animation.",
                            'Le dernier forfait confirmé.',
                            'Modification selon un nombre de participants ou un pourcentage. Se référer aux politiques du fournisseur pour plus de détails.',
                            'Nombre réel de participants.',
                        ]}
                        labelCol={LABEL_COL}
                        estPetit
                    />

                    <ChampFormulaire
                        id="guide_minimum_facturable_jour_anim_fournisseur"
                        titre="Min. fact. jour de l'animation (du fournisseur) INTERNE"
                        isCreatable
                        suggestions={[
                            "Le dernier nombre de participants confirmé sauf si plus élevé lors de l'animation.",
                            'Le dernier forfait confirmé.',
                            'Modification selon un nombre de participants ou un pourcentage. Se référer aux politiques du fournisseur pour plus de détails.',
                            'Nombre réel de participants.',
                        ]}
                        labelCol={LABEL_COL}
                        estPetit
                    />

                    <ChampFormulaire
                        id="temps_avance_animateur"
                        titre="Temps en avance des animateurs"
                        labelCol={LABEL_COL}
                        estPetit
                    />
                    <ChampFormulaire
                        id="ratio_groupe"
                        titre="Ratio et formation de groupe"
                        textarea
                        labelCol={LABEL_COL}
                    />
                    <ChampFormulaire id="locaux" titre="Locaux" textarea labelCol={LABEL_COL} />
                    <ChampsMultipleLangue id="langues" labelCol={LABEL_COL} avecUnBouton />
                    <ChampFormulaire
                        id="guide_changement_prix"
                        titre="Changement de prix"
                        isCreatable
                        suggestions={['Aucune modification', 'Possible']}
                        labelCol={LABEL_COL}
                        estPetit
                    />
                </MyPaper>
                <MyPaper>
                    <ChampFormulaire id="horaire_type" titre="Horaire type" textarea labelCol={LABEL_COL} />
                    <ChampFormulaire
                        id="guide_preparation"
                        titre="Comment bien vous préparer à l’activité"
                        textarea
                        labelCol={LABEL_COL}
                    />
                    <ChampFormulaire
                        id="questionnaire_logistique"
                        titre="Questionnaire logistique"
                        textarea
                        labelCol={LABEL_COL}
                    />
                    <ChampFormulaire
                        id="guide_clauses_politiques"
                        titre="Clauses et politiques"
                        textarea
                        labelCol={LABEL_COL}
                    />
                    <ChampFormulaire id="guide_FAQ" titre="FAQ" textarea labelCol={LABEL_COL} />
                </MyPaper>
                <MyPaper>
                    <ChampFichier
                        id="piece_jointe"
                        titre="PDF additionnel"
                        accept="application/pdf"
                        labelCol={LABEL_COL}
                        estPetit
                    />
                </MyPaper>
                <MyPaper>
                    <ChampMultipleModelesHoraireGlissable
                        labelCol={LABEL_COL}
                        id="modelesHorairesActivite"
                        horaireParDefautId={values.fk_id_horaire_par_defaut}
                    />
                </MyPaper>
            </div>
            <ModalDetails
                afficher={afficherModalDetailsLieu}
                setAfficher={() => setAfficherModalDetailsLieu(false)}
                detailId={formikAvoirDetailLieu(indexModalDetailsLieu)}
                titre={'Détails sur le lieu'}
                suggestions={[]}
            />
            <ModalDetails
                afficher={afficherModalContactActivite}
                setAfficher={() => setAfficherModalContactActivite(false)}
                detailId={formikAvoirDetailContact(indexModalContactActivite)}
                titre={'Détails sur le contact'}
                suggestions={SUGGESTIONS_CONTACT}
            />
        </>
    );
}

export const beforeSaveActivite = async (activite: Activite, helpers: FormikHelpers<Activite>): Promise<Activite> => {
    // gestion des ajouts d'images
    if (activite.fichiers) {
        const images: Images[] = await Promise.all(
            activite.fichiers.map(async (img) => {
                if (img.fichiers.base64) {
                    const body = { ...img.fichiers };
                    const newFichier = await api
                        .post(`/${Fichier.key}`, {
                            ...body,
                            id: undefined,
                        })
                        .then((r) => r.data);
                    return { ...img, fk_id_fichier: newFichier.id };
                }
                return img;
            }),
        );

        helpers.setFieldValue('fichiers', images);

        if (activite.fk_id_image_principale < 0) {
            const index = activite.fichiers.findIndex((i) => i.fichiers.id === activite.fk_id_image_principale);
            if (index !== -1 && images[index]?.fk_id_fichier) {
                activite.fk_id_image_principale = images[index].fk_id_fichier;
                helpers.setFieldValue('fk_id_image_principale', images[index].fichiers.name);
            }
        }

        activite.fichiers = images;
    }

    // gestion de l'ajout de la piece jointe
    if (activite.piece_jointe && activite.piece_jointe.id == undefined) {
        activite.piece_jointe = await api
            .post(`/${Fichier.key}`, activite.piece_jointe)
            .then((r: AxiosResponse<Fichier>) => r.data);
        helpers.setFieldValue('piece_jointe', activite.piece_jointe);
        helpers.setFieldValue('fk_id_fichier_annexe', activite.piece_jointe.id);
    }
    if (!activite.piece_jointe) {
        helpers.setFieldValue('fk_id_fichier_annexe', undefined);
    }
    if (activite.methode_paiement) {
        helpers.setFieldValue('methode_paiement', activite.methode_paiement.name);
    }

    return activite;
};

/**
 * Pour pouvoir assigner les postes (qui est une entité client-contact) dans un champ
 * multiple qui est une entité client.
 * @param index position dans le tableau des contacts
 */
function formikAvoirDetailContact(index: number): string {
    return `contacts[${index}].detail`;
}
function formikAvoirDetailLieu(index: number): string {
    return `lieux[${index}].detail`;
}

function formikAvoirDetailProduit(index: number): string {
    return `produits[${index}].detail`;
}

export default function DetailsActivite(): ReactElement {
    const [masquerCovid19, setMasquerCovid19] = useRecoilState(etatMasquerCovid19);

    const { id } = useParams() as { id: string };
    const listLogs = useGetQuery<HistoriqueLogs[]>(`historique_logs/${id}/activite`);

    const actionsActivite = (value: Activite) => {
        function InnerActions() {
            const { name, url, loading } = useLoadPdf(value, masquerCovid19.estMasquer);

            return (
                <>
                    <Dropdown.Item href={`/activites/${String(value.id)}`} target="_blank">
                        Ouvrir la page de l&apos;activité
                    </Dropdown.Item>
                    <Dropdown.Item href={url} disabled={loading} download={name}>
                        {loading ? 'Chargement...' : 'Télécharger le PDF'}
                    </Dropdown.Item>
                </>
            );
        }
        return <InnerActions />;
    };

    return (
        <FormulaireModificationEntite
            id={+id}
            entite={Activite}
            badges={(a: Activite) => [
                `Activité #${a.id}`,
                `Créée: ${DateTime.fromISO(a.created_at).toRelativeCalendar()}`,
                `Dernière modification: ${
                    listLogs.data?.[listLogs.data.length - 1]?.date_modification
                        ? dateFormatter(listLogs.data?.[listLogs.data.length - 1]?.date_modification)
                        : 'Aucune modificiation'
                }  ${
                    listLogs.data?.[listLogs.data.length - 1]?.nom_usager
                        ? ' par ' + listLogs.data?.[listLogs.data.length - 1]?.nom_usager
                        : ''
                }`,
            ]}
            schema={schemaActivite}
            actions={actionsActivite}
            onBeforeSave={beforeSaveActivite}
            properties_to_reassign={{
                fournisseur: 'fk_id_fournisseur',
                contact_par_defaut: 'fk_id_contact_par_defaut',
                adresse_calcul_km: 'fk_id_lieu',
            }}
        >
            <ChampsActivites />
            <LogsLoader id={id} tableName={'activite'} />
        </FormulaireModificationEntite>
    );
}
